<template>
    <v-dialog scrollable v-model="dialog" transition="dialog-bottom-transition" persistent no-click-animation
        max-width="1500">
        <v-card class="card-container" min-height="650">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ editMode ? $t('common.edit_prescription') : $t('common.add_prescription') }}
            </v-card-title>
            <v-card-text class="pt-3 px-3">
                <v-row dense>
                    <!-- LOADING CATEGORIES -->
                    <v-col v-if="loadingCategories" cols="2" class="text-center">
                        <div class="container-full-center">
                            <loading-spinner />
                        </div>
                    </v-col>
                    <v-col v-else cols="1" class="text-center">
                        <div v-for="category in categoriesOptions" :key="category.id"
                            :class="['category-btn', { 'category-btn-selected': selectColorCategory(category) }, { 'black--text': !selectColorCategory(category) }]">
                            <button-tooltip @click="onClickCategory(category)"
                                :color="selectColorCategory(category) ? 'secondary' : 'primary'" large fab depressed
                                right :tooltip="category.name">
                                <v-icon x-large :color="selectColorCategory(category) ? '#FFFFFF' : '#000000'"
                                    v-text="category.icon" />
                            </button-tooltip>
                        </div>
                    </v-col>
                    <v-col v-if="!category" cols="11">
                        <v-alert color="secondary" outlined icon="mdi-alert-circle-outline" prominent border="left">
                            {{ $t('exercises.need_select_an_category') }}
                        </v-alert>
                    </v-col>
                    <v-col v-else cols="11">
                        <!-- "FILTERS" -->
                        <v-card color="grey lighten-4" flat pa-2>
                            <v-card-text class="px-4">
                                <v-row no-gutters justify="start" class="px-3" align="center">

                                    <!-- "NAME PRESCRIPTION" -->
                                    <v-col cols="6" md="3" lg="2" class="pa-1">
                                        <div class="label">{{ $t('common.name') }}</div>
                                        <v-text-field color="secondary" outlined dense v-model="name" hide-details />
                                    </v-col>

                                    <!-- "QUANTITY SETS" -->
                                    <v-col cols="6" md="2" lg="1" class="pa-1 mb-n2">
                                        <kimsa-combobox :label="$tc('exercises.sets', 2)" :value="set" outlined dense
                                            :items="setOptions" @change="set = $event" placeholder-class ref="set" />
                                    </v-col>

                                    <!-- "EACH SIDE" -->
                                    <v-col cols="6" md="3" lg="2" class="px-4 py-1 text-center">
                                        <v-switch dense :label="$t('exercises.each_side')" v-model="eachSide"
                                            :hint="$t('exercises.each_side_hint')" class="mb-3"
                                            persistent-hint></v-switch>
                                    </v-col>

                                    <!-- "PAIN SCALE" -->
                                    <v-col cols="6" md="3" lg="2" class="px-4 py-1 text-center">
                                        <v-switch dense :label="$t('common.pain_scale')" v-model="painScale"/>
                                    </v-col>

                                    <!-- "MAX WEIGHT" -->
                                    <v-col v-if="isStrongCategory" cols="2" class="pa-1">
                                        <div class="label">{{ $t('exercises.max_weight') }}</div>
                                        <v-text-field color="secondary" outlined dense type="number" min="0"
                                            @keypress="isNumber" inputmode="numeric" v-model="maxWeight" hide-details />
                                    </v-col>

                                    <!-- "PAUSE" -->
                                    <v-col cols="6" md="3" lg="2" class="pa-1 ">
                                        <div class="label">{{ $t('exercises.pause') }}</div>
                                        <v-btn height="40" small depressed color="secondary"
                                            @click="showRestMenu(!!time)">
                                            <template v-if="time">
                                                {{ time }}
                                                <v-icon size="18" class="ml-1">mdi-pencil</v-icon>
                                            </template>
                                            <template v-else>
                                                <v-icon>mdi-plus</v-icon>
                                            </template>
                                        </v-btn>
                                        <v-btn v-if="time" depressed x-small fab color="transparent">
                                            <v-icon @click="time = null" color="error">mdi-delete</v-icon>
                                        </v-btn>
                                    </v-col>

                                    <!-- "TAGS" -->
                                    <v-col cols="6" md="4" lg="2">
                                        <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                                            :label="$t('exercises.tags')" force-value :solo="false" />
                                    </v-col>
                                </v-row>

                            </v-card-text>
                        </v-card>

                        <!-- "SETS" -->
                        <v-card class="mt-2" flat color="grey lighten-4">
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" class="pa-1">
                                        <small class="caption">{{ $t('common.config') }}</small>
                                        <v-divider class="my-2"></v-divider>
                                        <!-- "REPS/TIME" -->
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1 pb-0">
                                                <kimsa-combobox :placeholder="$t('common.select_placeholder')" outlined
                                                    dense height="40" :items="repsOptions" @change="repsType = $event"
                                                    :value="repsType" placeholder-class
                                                    :icon="repsType && icons[repsType.value]" ref="repsType" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1">
                                                <v-text-field v-for="(item, i) in dataSet.repsType" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input" min="0"
                                                    @keypress="repsValidation" color="secondary" outlined dense
                                                    hide-details :readonly="!repsType" v-model="dataSet.repsType[i]"
                                                    @change="setSameValue(i, dataSet.repsType[i], 'repsType')" />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                    <v-col v-if="isStrongCategory" cols="12" class="pa-1 pt-0">
                                        <!-- "RPE/PERCENTAGE/RIR" -->
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1">
                                                <kimsa-combobox :placeholder="$t('common.select_placeholder')" outlined
                                                    dense height="40" :items="rpeOptions" @change="onChangeRpeType"
                                                    :value="rpeType" placeholder-class
                                                    :icon="rpeType && icons[rpeType.value]" ref="rpeType" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1">
                                                <v-text-field v-for="(item, i) in dataSet.rpeType" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input"
                                                    type="number" min="0" :max="maxValueRpeType"
                                                    @blur="maxValue('rpeType', maxValueRpeType(), i)"
                                                    @keypress="isNumber" color="secondary" :step="stepRpe(rpeType)"
                                                    outlined dense inputmode="numeric" hide-details :readonly="!rpeType"
                                                    v-model="dataSet.rpeType[i]"
                                                    @change="setSameValue(i, dataSet.rpeType[i], 'rpeType')" />
                                            </v-col>
                                        </v-row>
                                        <!-- "WEIGHT" -->
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1 pb-2">
                                                <v-text-field color="secondary" outlined dense inputmode="numeric"
                                                    :value="$t('exercises.weight')" readonly hide-details
                                                    :prepend-inner-icon="icons.weight"
                                                    :append-icon="disabledWeight ? 'mdi-check' : 'mdi-close'"
                                                    @click:append="onNoWeightOption" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1">
                                                <v-text-field v-for="(item, i) in dataSet.weight" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input"
                                                    color="secondary" outlined dense
                                                    :readonly="dataSet.weight[i] === '- s/p -'" hide-details
                                                    v-model="dataSet.weight[i]"
                                                    @change="setSameValue(i, dataSet.weight[i], 'weight')" />
                                            </v-col>
                                        </v-row>
                                        <!-- "TEMPO/ADDITIONAL" -->
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1 pt-2 pb-0">
                                                <kimsa-combobox :placeholder="$t('common.select_placeholder')" outlined
                                                    dense height="40" :items="tempoOptions" @change="onChangeTempoType"
                                                    :value="tempoType" placeholder-class
                                                    :icon="tempoType && icons[tempoType.value]" ref="tempoType" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1 pt-2 pb-0">
                                                <v-text-field v-for="(item, i) in dataSet.tempoType" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input" min="0"
                                                    :maxlength="tempoType && tempoType.value === 'tempo' ? undefined : 3"
                                                    @keypress="tempoValidation" color="secondary" outlined dense
                                                    inputmode="numeric" hide-details :readonly="!tempoType"
                                                    v-model="dataSet.tempoType[i]"
                                                    @change="setSameValue(i, dataSet.tempoType[i], 'tempoType')" />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                    <!-- "INTENSITY" -->
                                    <v-col v-if="hasIntensity" cols="12" class="pa-1">
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1 pb-0">
                                                <kimsa-combobox :placeholder="$t('common.select_placeholder')" outlined
                                                    dense height="40" :items="intensityOptions"
                                                    @change="onChangeIntensityType" :value="intensityType"
                                                    placeholder-class
                                                    :icon="intensityType && icons[intensityType.value]"
                                                    ref="intensityType" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1">
                                                <v-text-field v-for="(item, i) in dataSet.intensityType" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input"
                                                    color="secondary" @keypress="intensityValidation"
                                                    :type="intensityType && intensityType.value === 'rpe' ? 'number' : 'text'"
                                                    :step="intensityType && intensityType.value === 'rpe' ? 0.5 : 1"
                                                    outlined dense hide-details v-model="dataSet.intensityType[i]"
                                                    @change="setSameValue(i, dataSet.intensityType[i], 'intensityType')" />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                    <!-- "MICRO PAUSE" -->
                                    <v-col v-if="hasMicroPause" cols="12" class="pa-1">
                                        <v-row no-gutters>
                                            <v-col cols="2" class="pa-1 pb-0">
                                                <v-text-field color="secondary" outlined dense inputmode="numeric"
                                                    :value="$t('exercises.micropause')" readonly hide-details
                                                    :prepend-inner-icon="icons.micropause" />
                                            </v-col>
                                            <v-col cols="10" class="pa-1">
                                                <v-text-field v-for="(item, i) in dataSet.micropause" :key="i"
                                                    :label="`${$t('exercises.set')} ${i + 1}`" class="set-input" min="0"
                                                    @keypress="minuteSecondsValidation" color="secondary" outlined dense
                                                    hide-details v-model="dataSet.micropause[i]"
                                                    @change="setSameValue(i, dataSet.micropause[i], 'micropause')" />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                        <v-divider class="mt-2" />
                        <!-- COMMENTS -->
                        <v-row>
                            <v-col cols="12">
                                <v-textarea rows="1" v-model="commentary" color="secondary" outlined auto-grow
                                    placeholder="Comentario" hide-details />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabledAdd" @click="add" :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
            <rest-dialog as-menu is-second-dialog @save="time = $event" ref="rest" :value="time" />
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import RestDialog from "@/components/coach/routines/RestDialog";
import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';

export default {
    name: "CreatePrescriptionDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    components: {
        ButtonTooltip,
        LoadingSpinner,
        KimsaTextField,
        RestDialog,
        KimsaCombobox,
        KimsaTagsPicker
    },
    data(vm) {
        return {
            dialog: false,
            dataSetValid: false,
            editMode: false,
            prescriptionId: null,
            loading: false,
            category: null,
            name: '',
            setOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            set: 1,
            time: null,
            eachSide: false,
            painScale: false,
            // DATA OF SETS
            maxWeight: null,
            repsType: null,
            rpeType: null,
            tempoType: null,
            intensityType: null,
            dataSet: {},
            commentary: '',
            rpeOptions: [
                { name: vm.$t('exercises.rpe'), value: 'rpe' },
                { name: vm.$t('exercises.percentage'), value: 'percentage' },
                { name: vm.$t('exercises.rir'), value: 'rir' },
                { name: vm.$t('exercises.meter_seconds'), value: 'meter_seconds' },
            ],
            tempoOptions: [
                { name: vm.$t('exercises.tempo'), value: 'tempo' },
                { name: vm.$t('exercises.additional'), value: 'additional' }
            ],
            intensityOptions: [
                { name: vm.$t('exercises.beat_min'), value: 'beat_min' },
                { name: vm.$t('exercises.time_km'), value: 'time_km' },
                { name: vm.$t('exercises.km'), value: 'km' },
                { name: vm.$t('exercises.rpe'), value: 'rpe' },
                { name: vm.$t('exercises.time'), value: 'time' },
                { name: vm.$t('exercises.meter_seconds'), value: 'meter_seconds' },
                { name: vm.$t('exercises.percentage_mas'), value: 'percentage_mas' },
                { name: vm.$t('exercises.percentage_mss'), value: 'percentage_mss' },
            ],
            icons: {
                reps: 'mdi-repeat',
                time: 'mdi-av-timer',
                minutes: 'mdi-clock-outline',
                distance: 'mdi-diameter-outline',
                weight: 'mdi-weight',
                rpe: 'mdi-chart-box-outline',
                km: 'mdi-speedometer',
                percentage: 'mdi-percent-outline',
                rir: 'mdi-cached',
                meter_seconds: 'mdi-diameter',
                tempo: 'mdi-timer-outline',
                additional: 'mdi-alarm-plus',
                intensity: 'mdi-chart-timeline-variant-shimmer',
                micropause: 'mdi-timer-off-outline',
                beat_min: 'mdi-heart-pulse',
                time_km: 'mdi-gauge',
                percentage_mas: 'mdi-run',
                percentage_mss: 'mdi-run-fast'
            },
            disabledWeight: false,
            tags: [],
        }
    },
    computed: {
        ...mapState(['dialogActive', 'categoriesOptions', "loadingCategories"]),
        repsOptions(vm = this) {
            if (!vm.hasIntensity) return [
                { name: vm.$t('exercises.reps'), value: 'reps' },
                { name: vm.$t('exercises.time'), value: 'time' },
                { name: vm.$t('exercises.distance'), value: 'distance' }
            ]
            return [
                { name: vm.$t('exercises.reps'), value: 'reps' },
                { name: vm.$t('exercises.minutes'), value: 'minutes' },
                { name: vm.$t('exercises.distance'), value: 'distance' }
            ]
        },
        isStrongCategory(vm = this) {
            return vm.category?.name === vm.$t('objectives.strong')
        },
        hasMicroPause(vm = this) {
            return vm.category?.name !== vm.$t('objectives.stability') || vm.category?.name !== vm.$t('objectives.mobility')
        },
        hasIntensity(vm = this) {
            return vm.category?.name === vm.$t('objectives.speed') || vm.category?.name === vm.$t('objectives.resistance')
        },
        disabledAdd(vm = this) {
            return !vm.name || !vm.category || !vm.set || !vm.repsType
        },
        isCompany(vm = this) {
            return vm.accountType === 'company'
        },
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['fetchExerciseCategories']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.category = null
            vm.editMode = false
            vm.prescriptionId = null
            vm.reset()
        },
        reset() {
            const vm = this
            vm.name = ''
            vm.set = 1
            vm.time = null
            vm.eachSide = false
            vm.painScale = false
            vm.maxWeight = null
            vm.repsType = null
            vm.rpeType = null
            vm.tempoType = null
            vm.intensityType = null
            vm.commentary = ''
            vm.dataSet = {}
            vm.dataSetValid = false
            vm.tags = []
        },
        setEditingMode(prescription) {
            const vm = this
            vm.editMode = true
            vm.loadingEditMode = true
            vm.show()

            console.log('setEditingMode', prescription)

            vm.prescriptionId = prescription.objectID || prescription.id
            vm.name = prescription.name
            vm.category = vm.categoriesOptions.find(category => category.id === prescription.category)
            vm.set = prescription?.config?.sets.length || null
            vm.time = prescription.time || ''
            vm.eachSide = prescription.eachSide || false
            vm.painScale = prescription.painScale || false
            vm.maxWeight = prescription.maxWeight || null
            vm.commentary = prescription.commentary || ''
            vm.tags = prescription.tags || []

            vm.loadingEditMode = false
            let dataSet = vm.populateConfig(prescription.config)

            vm.$nextTick(() => {
                vm.dataSet = dataSet
            })
        },
        populateConfig(config) {
            const vm = this
            let dataSet = {}
            if (config) {
                config.sets.forEach((set, i) => {
                    if (i === 0) {
                        // INITIALIZE
                        let repsType = null
                        if (vm.hasIntensity) {
                            repsType = set?.data.find(data =>
                                data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.minutes') || data.name === vm.$t('exercises.distance'))
                        } else {
                            repsType = set?.data.find(data =>
                                data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.time') ||
                                data.name === vm.$t('exercises.distance'))
                        }
                        vm.repsType = repsType && vm.repsOptions.find(data => data.name === repsType.name) || null
                        if (repsType) dataSet.repsType = []
                        let rpeType = vm.isStrongCategory && set.data.find(data =>
                            data.name === vm.$t('exercises.rpe') || data.name === vm.$t('exercises.percentage') ||
                            data.name === vm.$t('exercises.rir') || data.name === vm.$t('exercises.meter_seconds'))
                        vm.rpeType = rpeType && vm.rpeOptions.find(data => data.name === rpeType.name) || null
                        if (rpeType) dataSet.rpeType = []
                        let tempoType = set.data.find(data => data.name === vm.$t('exercises.tempo') || data.name === vm.$t('exercises.additional'))
                        vm.tempoType = tempoType && vm.tempoOptions.find(data => data.name === tempoType.name) || null
                        if (tempoType) dataSet.tempoType = []
                        if (set.data.find(data => data.name === vm.$t('exercises.weight')) || vm.isStrongCategory) dataSet.weight = []
                        let intensityType = vm.hasIntensity && set.data.find(data => data.name === vm.$t('exercises.beat_min') || data.name === vm.$t('exercises.time_km') ||
                            data.name === vm.$t('exercises.km') || data.name === vm.$t('exercises.rpe') || data.name === vm.$t('exercises.time') ||
                            data.name === vm.$t('exercises.meter_seconds') || data.name === vm.$t('exercises.percentage_mas') || data.name === vm.$t('exercises.percentage_mss'))
                        vm.intensityType = intensityType && vm.intensityOptions.find(data => data.name === intensityType.name) || null
                        if (intensityType) dataSet.intensityType = []
                        if (set.data.find(data => data.name === vm.$t('exercises.micropause'))) dataSet.micropause = []
                    }

                    set.data.forEach((data, j) => {
                        // POPULATE
                        if (vm.repsType &&
                            (
                                data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.minutes') || data.name === vm.$t('exercises.distance') ||
                                (!vm.hasIntensity && data.name === vm.$t('exercises.time'))
                            )
                        ) {
                            dataSet.repsType.push(data.value)
                        }
                        if (vm.rpeType &&
                            (
                                data.name === vm.$t('exercises.rpe') ||
                                data.name === vm.$t('exercises.percentage') ||
                                data.name === vm.$t('exercises.rir') ||
                                data.name === vm.$t('exercises.meter_seconds')
                            )
                        ) {
                            dataSet.rpeType.push(data.value)
                        }
                        if (vm.tempoType &&
                            (
                                data.name === vm.$t('exercises.tempo') ||
                                data.name === vm.$t('exercises.additional')
                            )
                        ) {
                            dataSet.tempoType.push(data.value)
                        }
                        if (dataSet.weight && data.name === vm.$t('exercises.weight')) dataSet.weight.push(data.value)
                        if (vm.intensityType &&
                            (
                                data.name === vm.$t('exercises.beat_min') ||
                                data.name === vm.$t('exercises.time_km') ||
                                data.name === vm.$t('exercises.km') ||
                                data.name === vm.$t('exercises.rpe') ||
                                data.name === vm.$t('exercises.time') ||
                                data.name === vm.$t('exercises.meter_seconds') ||
                                data.name === vm.$t('exercises.percentage_mas') ||
                                data.name === vm.$t('exercises.percentage_mss')
                            )
                        ) {
                            dataSet.intensityType.push(data.value)
                        }
                        if (dataSet.micropause && data.name === vm.$t('exercises.micropause')) dataSet.micropause.push(data.value)
                    })
                })
                if (vm.isStrongCategory && dataSet.weight?.length === 0) dataSet.repsType.forEach(() => {
                    dataSet.weight.push(0)
                })
            } else {
                vm.$nextTick(() => {
                    vm.set = 1
                })
            }
            return dataSet
        },

        // CATEGORY
        onClickCategory(category) {
            const vm = this
            vm.category = category
        },
        selectColorCategory(category) {
            return this.category?.name === category.name
        },

        // ADD/EDIT REST
        showRestMenu(edit) {
            const vm = this
            edit ? vm.$refs.rest.showEdit(vm.time) :
                vm.$refs.rest.show()
        },

        // SETS
        addQuantitySet(qty, value = null) {
            return Array.from({ length: qty }, () => value);
        },
        setDataSet(qty, oldQty = null) {
            const vm = this
            if (!vm.category) return {}
            let category = vm.category.name
            let payload = vm.dataSet

            if (oldQty) {
                if (qty > oldQty) {
                    let newQty = qty - oldQty
                    if (payload.repsType) payload.repsType.push(...vm.addQuantitySet(newQty, payload.repsType[0]))
                    if (category === vm.$t('objectives.strong')) {
                        if (payload.rpeType) payload.rpeType.push(...vm.addQuantitySet(newQty, payload.rpeType[0]))
                        if (payload.weight) payload.weight.push(...vm.addQuantitySet(newQty, payload.weight[0]))
                        if (payload.tempoType) payload.tempoType.push(...vm.addQuantitySet(newQty, payload.tempoType[0]))
                    }
                    if (category === vm.$t('objectives.speed') || category === vm.$t('objectives.resistance')) {
                        if (payload.intensityType) payload.intensityType.push(...vm.addQuantitySet(newQty, payload.intensityType[0]))
                    }
                    if (category !== vm.$t('objectives.stability') || category !== vm.$t('objectives.mobility')) {
                        if (payload.micropause) payload.micropause.push(...vm.addQuantitySet(newQty, payload.micropause[0]))
                    }
                } else {
                    if (JSON.stringify(payload) === "{}") {
                        payload.repsType = vm.addQuantitySet(qty, 0)
                        if (category === vm.$t('objectives.strong')) {
                            payload.rpeType = vm.addQuantitySet(qty, 0)
                            payload.weight = vm.addQuantitySet(qty, 0)
                            payload.tempoType = vm.addQuantitySet(qty, 0)
                        }
                        if (category === vm.$t('objectives.speed') || category === vm.$t('objectives.resistance')) {
                            payload.intensityType = vm.addQuantitySet(qty, 0)
                        }
                        if (category !== vm.$t('objectives.stability') || category !== vm.$t('objectives.mobility')) {
                            payload.micropause = vm.addQuantitySet(qty, 0)
                        }
                    } else {
                        payload.repsType = payload.repsType && payload.repsType.slice(0, qty)
                        if (category === vm.$t('objectives.strong')) {
                            payload.rpeType = payload.rpeType && payload.rpeType.slice(0, qty)
                            payload.weight = payload.weight && payload.weight.slice(0, qty)
                            payload.tempoType = payload.tempoType && payload.tempoType.slice(0, qty)
                        }
                        if (category === vm.$t('objectives.speed') || category === vm.$t('objectives.resistance')) {
                            payload.intensityType = payload.intensityType && payload.intensityType.slice(0, qty)
                        }
                        if (category !== vm.$t('objectives.stability') || category !== vm.$t('objectives.mobility')) {
                            payload.micropause = payload.micropause && payload.micropause.slice('00:00', qty)
                        }
                    }
                }
            } else {
                payload.repsType = vm.addQuantitySet(qty, 0)
                if (category === vm.$t('objectives.strong')) {
                    payload.rpeType = vm.addQuantitySet(qty, 0)
                    payload.weight = vm.addQuantitySet(qty, 0)
                    payload.tempoType = vm.addQuantitySet(qty, 0)
                }
                if (category === vm.$t('objectives.speed') || category === vm.$t('objectives.resistance')) {
                    payload.intensityType = vm.addQuantitySet(qty, 0)
                }
                if (category !== vm.$t('objectives.stability') || category !== vm.$t('objectives.mobility')) {
                    payload.micropause = vm.addQuantitySet(qty, '00:00')
                }
            }

            vm.dataSet = payload
            // console.log('dataSet', payload)
        },
        setSameValue(index, value, attribute) {
            const vm = this;
            if (index !== 0) return
            let dataSet = { ...vm.dataSet }
            dataSet[attribute] = vm.addQuantitySet(vm.set, value)
            vm.dataSet = dataSet
        },
        onNoWeightOption() {
            const vm = this
            if (vm.maxWeight) return
            vm.disabledWeight = !vm.disabledWeight
            let value = vm.disabledWeight ? '- s/p -' : ''
            vm.dataSet.weight.forEach((item, i) => {
                vm.setSameValue(i, value, 'weight')
            })
        },
        calculateWeight() {
            const vm = this
            let maxWeight = Number(vm.maxWeight || 0)
            if (vm.rpeType?.value === 'percentage' && maxWeight > 0)
                vm.dataSet.rpeType.forEach((percentage, i) => {
                    vm.dataSet.weight[i] = Math.round((maxWeight * percentage) / 100)
                })
        },
        stepRpe(rpeType) {
            if (rpeType?.value === 'meter_seconds') return 0.1
            if (rpeType?.value === 'rpe') return 0.5
            return 1
        },

        // VALIDATIONS
        isNumber(event) {
            const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-']
            if (!allowedKeys.includes(event.key) && event.key !== 'Backspace') event.preventDefault()
        },
        tempoValidation(event) {
            const vm = this;
            const char = event.key;
            const isNumber = char >= '0' && char <= '9';
            const isDecimalOrHyphen = char === '.' || char === '-';
            const isLetter = (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z');
            const isBackspace = char === 'Backspace';
            if (vm.tempoType?.value === 'tempo') {
                if (!isNumber && !isDecimalOrHyphen && !isBackspace) event.preventDefault()
            } else {
                if (!isLetter && !isDecimalOrHyphen && !isBackspace) event.preventDefault()
            }
        },
        intensityValidation(event) {
            const vm = this;
            const char = event.key
            if (vm.intensityType?.value === 'time' || vm.intensityType?.value === 'time_km') return vm.minuteSecondsValidation(event)
            const isNumber = char >= '0' && char <= '9'
            const isLetter = (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')
            const isSlash = char === '/'
            const isBackspace = char === 'Backspace'
            if (!isNumber && !isLetter && !isSlash && !isBackspace) event.preventDefault()
        },
        minuteSecondsValidation(event) {
            const char = event.key;
            const isNumber = char >= '0' && char <= '9'
            const isColon = char === ':'
            const isBackspace = char === 'Backspace'
            if (!isNumber && !isColon && !isBackspace) event.preventDefault()
        },
        repsValidation(event) {
            const char = event.key
            const allowedChars = '0123456789()maxdropy -:'
            const isAllowed = allowedChars.includes(char.toLowerCase()) || char === 'Backspace'
            if (!isAllowed) event.preventDefault()
        },
        isDataSetValid() {
            const vm = this
            let valid = true
            if (!vm.dataSet) return vm.dataSetValid = false
            if (vm.repsType && vm.dataSet.repsType) vm.dataSet.repsType.forEach(reps => {
                if (!reps && reps !== 0) return valid = false
            })
            if (vm.rpeType && vm.dataSet.rpeType) vm.dataSet.rpeType.forEach(rpe => {
                if (!rpe && rpe !== 0) return valid = false
            })
            if (vm.tempoType && vm.dataSet.tempoType) vm.dataSet.tempoType.forEach(tempo => {
                if (!tempo && tempo !== 0) return valid = false
            })
            if (vm.dataSet.weight) vm.dataSet.weight.forEach(weight => {
                if (!weight && weight !== 0) return valid = false
            })
            if (vm.hasIntensity && vm.dataSet.intensityType) vm.dataSet.intensityType.forEach(it => {
                if (!it && it !== 0) return valid = false
            })
            if (vm.hasMicroPause && vm.dataSet.micropause) vm.dataSet.micropause.forEach(mp => {
                if (!mp && mp !== 0) return valid = false
            })
            vm.dataSetValid = valid
        },
        maxValue(attribute, max, index) {
            const vm = this
            if (max === undefined) return
            if (vm.dataSet[attribute][index] > max) {
                let dataSet = { ...vm.dataSet }
                dataSet[attribute][index] = max
                vm.dataSet = dataSet
            }
        },
        maxValueRpeType() {
            const vm = this
            if (vm.rpeType?.value === 'percentage') return 100
            if (vm.rpeType?.value === 'rir' || vm.rpeType?.value === 'rpe') return 10
            return undefined
        },

        // CHANGE OPTIONS
        onChangeRpeType(type) {
            const vm = this;
            vm.rpeType = type
            let dataSet = { ...vm.dataSet }
            dataSet.rpeType = vm.addQuantitySet(vm.set, 0)
            vm.dataSet = dataSet
        },
        onChangeTempoType(type) {
            const vm = this;
            vm.tempoType = type
            let dataSet = { ...vm.dataSet }
            let value = type.value === 'tempo' ? 0 : ''
            dataSet.tempoType = vm.addQuantitySet(vm.set, value)
            vm.dataSet = dataSet
        },
        onChangeIntensityType(type) {
            const vm = this
            vm.intensityType = type
            let dataSet = { ...vm.dataSet }
            let value = (type?.value === 'time' || type?.value === 'time_km') ? '00:00' : ''
            dataSet.intensityType = vm.addQuantitySet(vm.set, value)
            vm.dataSet = dataSet
        },

        // ADD/EDIT
        generateConfig() {
            const vm = this
            let config = { sets: [] }
            const typesToAdd = [
                { prop: 'repsType', name: 'exercises.reps' },
                { prop: 'rpeType', name: 'exercises.rpe' },
                { prop: 'weight', name: 'exercises.weight', alwaysAdd: vm.isStrongCategory },
                { prop: 'tempoType', name: 'exercises.tempo' },
                { prop: 'intensityType', name: 'exercises.intensity' },
                { prop: 'micropause', name: 'exercises.micropause', alwaysAdd: true }
            ]
            for (let i = 0; i < vm.set; i++) {
                let data = []
                typesToAdd.forEach(({ prop, name, alwaysAdd }) => {
                    if (alwaysAdd || (vm[prop] && vm.dataSet[prop])) {
                        data.push({
                            name: vm[prop]?.name || vm.$t(name),
                            short_name: `short_${vm[prop]?.value || prop}`,
                            value: vm.dataSet[prop][i],
                            icon: vm.icons[vm[prop]?.value || prop]
                        })
                    }
                })

                config.sets.push({ data })
            }
            return config
        },
        async add() {
            const vm = this
            vm.loading = true

            let payload = {
                name: vm.name,
                commentary: vm.commentary,
                config: vm.generateConfig(),
                eachSide: vm.eachSide,
                painScale: vm.painScale,
                hasTime: !!vm.time,
                time: vm.time,
                coachId: vm.coachId,
                category: vm.category.id,
                categoryName: vm.category.name,
                tags: vm.tags,
            }
            if (vm.isStrongCategory) payload.maxWeight = vm.maxWeight

            console.log('payload', payload);

            if (vm.editMode) {
                await db.collection('prescriptions').doc(vm.prescriptionId).update(payload)
            } else {
                await db.collection('prescriptions').add(payload)
            }

            vm.loading = false
            vm.$emit('success')
            vm.toast(vm.$t('common.saved_prescription'))
            vm.hide()
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        types() {
            const vm = this
            if (!vm.firstTime) vm.exercise = null
            vm.loadExercises()
        },
        set(val, oldVal) {
            const vm = this
            vm.setDataSet(val, oldVal)
            vm.isDataSetValid()
        },
        category(val, oldVal) {
            const vm = this
            if (oldVal !== null) vm.reset()
            if (!vm.set) return
            vm.setDataSet(vm.set)
        },
        dataSet: {
            deep: true,
            handler() {
                this.isDataSetValid()
                this.calculateWeight()
            }
        },
        repsType() {
            this.isDataSetValid()
        },
        rpeType() {
            this.isDataSetValid()
        },
        tempoType() {
            this.isDataSetValid()
        },
        maxWeight() {
            this.calculateWeight()
        },
    }
}
</script>

<style scoped>
.set-input {
    width: 75px;
    display: inline-block;
    margin: 0 2px;
}

.category-btn {
    cursor: pointer;
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 4px;
    border: 1px solid var(--v-secondary-base) !important;
    border-radius: 50%;
    display: inline-block;
}

.category-btn-selected {
    border: 1px solid var(--v-primary-base) !important;
}

.container-full-center {
    height: 100%;
    display: flex;
    align-items: center;
}

.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}
</style>